'use client';

import { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { useDealFilterOptions } from '@/hooks/useDealFilterOptions';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import ErrorAlert from '@/components/ui/ErrorAlert';
import DealFilterForm from '@/components/filters/DealFilterForm';
import EnhancedDealTable from '@/components/deals/EnhancedDealTable';
import BackButton from '@/components/ui/BackButton';
import ClientSearch from '@/components/deals/ClientSearch';
import { useDeals } from '@/hooks/useDeals';

export default function DealsPage() {
  const { filterOptions, loading: loadingFilters, error: filterError } = useDealFilterOptions();
  const { stocks, loading: loadingStocks, error: stocksError, fetchDeals } = useDeals();
  const [hasSearched, setHasSearched] = useState(false);

  const handleFilterSubmit = async (filters: Record<string, unknown>) => {
    // Filter to only include Bulk and Block deals
    const bulkBlockFilters = {
      ...filters,
      deal_type: (() => {
        // Handle the deal_type field properly
        if (filters.deal_type && typeof filters.deal_type === 'string' && filters.deal_type.trim() !== '') {
          // If deal_type is provided as a comma-separated string
          const dealTypes = filters.deal_type.split(',').map(type => type.trim());
          const allowedTypes = dealTypes.filter(type => type === 'Bulk' || type === 'Block');
          return allowedTypes.length > 0 ? allowedTypes.join(',') : 'Bulk,Block';
        } else if (Array.isArray(filters.deal_type)) {
          // If deal_type is provided as an array
          const allowedTypes = filters.deal_type.filter((type: string) => type === 'Bulk' || type === 'Block');
          return allowedTypes.length > 0 ? allowedTypes.join(',') : 'Bulk,Block';
        } else {
          // Default to Bulk,Block if no deal_type specified
          return 'Bulk,Block';
        }
      })()
    };
    
    await fetchDeals(bulkBlockFilters);
    setHasSearched(true);
  };

  // Removed the automatic search functionality
  
  const error = filterError || stocksError;

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <header className="mb-6 flex items-start gap-3">
          <div className="mt-1">
            <BackButton href="/tools" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Bulk & Block Deals Analysis</h1>
            <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">Large negotiated trades across NSE and BSE</p>
          </div>
        </header>
      
        {error && <ErrorAlert message={error} />}
      
        <main>
          {/* Client Search Section */}
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-1">Quick Client Search</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
              Search for specific client deals across all stocks and deal types.
            </p>
            <ClientSearch className="max-w-md" />
          </Card>

          <Card className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Filter Deals</h2>
              {hasSearched && stocks.length > 0 && (
                <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  Found {stocks.length} stock{stocks.length !== 1 ? 's' : ''} with deals
                </span>
              )}
            </div>
          
            {loadingFilters ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
                <span className="ml-3 text-zinc-500 dark:text-zinc-400">Loading filters...</span>
              </div>
            ) : filterOptions ? (
              <DealFilterForm 
                filterOptions={{
                  ...filterOptions,
                  deal_type: ['Bulk', 'Block'] // Only show Bulk and Block options
                }} 
                onSubmit={handleFilterSubmit} 
                isLoading={loadingStocks}
              />
            ) : (
              <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                Failed to load filter options. Please refresh the page.
              </div>
            )}
          </Card>
        
          {loadingStocks ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
              <span className="ml-4 text-zinc-500 dark:text-zinc-400">Loading deals data...</span>
            </div>
          ) : (
            hasSearched && (
              <>
                {stocks.length > 0 ? (
                  <div className="rounded-2xl border border-zinc-200 bg-white p-4 md:p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Bulk & Block Deals Analysis</h2>
                    </div>
                  
                    <EnhancedDealTable stocks={stocks} />
                  </div>
                ) : (
                  <Card>
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="mt-2 text-lg font-medium text-zinc-900 dark:text-white">No results found</h3>
                      <p className="mt-1 text-zinc-500 dark:text-zinc-400">
                        No deals found matching your criteria. Try adjusting your filters.
                      </p>
                    </div>
                  </Card>
                )}
              </>
            )
          )}
        </main>
      </div>
    </PageLayout>
  );
} 